import { useEffect, useId, useRef } from "react";

import { Icon } from "./Icon.jsx";

export function Dialog({ children, className = "", onClose, title }) {
  const dialogRef = useRef(null);
  const titleId = useId();

  useEffect(() => {
    function handleKeyDown(event) {
      if (event.key === "Escape") {
        onClose();
        return;
      }

      if (event.key !== "Tab" || !dialogRef.current) return;

      const focusableElements = dialogRef.current.querySelectorAll(
        [
          "a[href]",
          "button:not([disabled])",
          "textarea:not([disabled])",
          "input:not([disabled])",
          "select:not([disabled])",
          "[tabindex]:not([tabindex='-1'])",
        ].join(","),
      );
      const focusable = [...focusableElements];

      if (!focusable.length) {
        event.preventDefault();
        dialogRef.current.focus();
        return;
      }

      const firstElement = focusable[0];
      const lastElement = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  useEffect(() => {
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    const firstFocusable = dialogRef.current?.querySelector(
      [
        "button:not([disabled])",
        "a[href]",
        "textarea:not([disabled])",
        "input:not([disabled])",
        "select:not([disabled])",
        "[tabindex]:not([tabindex='-1'])",
      ].join(","),
    );

    document.body.style.overflow = "hidden";

    window.requestAnimationFrame(() => {
      (firstFocusable || dialogRef.current)?.focus();
    });

    return () => {
      document.body.style.overflow = previousOverflow;
      if (previousFocus && typeof previousFocus.focus === "function") {
        previousFocus.focus();
      }
    };
  }, []);

  return (
    <div className="dialog-backdrop" role="presentation">
      <section
        aria-labelledby={titleId}
        aria-modal="true"
        className={`dialog-card ${className}`.trim()}
        ref={dialogRef}
        role="dialog"
        tabIndex="-1"
      >
        <header className="dialog-card__header">
          <h2 id={titleId}>{title}</h2>
          <button
            aria-label="Tutup"
            className="dialog-close"
            onClick={onClose}
            type="button"
          >
            <Icon name="close" size={18} />
          </button>
        </header>
        {children}
      </section>
    </div>
  );
}

export function ConfirmDialog({
  busy = false,
  busyLabel = "Menghapus…",
  confirmLabel = "Hapus",
  description,
  onCancel,
  onConfirm,
  title = "Konfirmasi Hapus",
}) {
  return (
    <Dialog onClose={onCancel} title={title}>
      <div className="dialog-card__body">
        <p>{description}</p>
      </div>
      <footer className="dialog-card__actions">
        <button
          className="secondary-button"
          disabled={busy}
          onClick={onCancel}
          type="button"
        >
          Batal
        </button>
        <button
          className="danger-button"
          disabled={busy}
          onClick={onConfirm}
          type="button"
        >
          {busy ? busyLabel : confirmLabel}
        </button>
      </footer>
    </Dialog>
  );
}
